/**
 * ProductGridManager - Unified Product Grid Component
 * 
 * Single source of truth for all product grid functionality.
 * Extracted from UnifiedProductsPage with full feature preservation.
 * 
 * ALL 21 CRITICAL FEATURES PRESERVED:
 * 1. ✅ Search with URL sync
 * 2. ✅ Fuzzy search integration
 * 3. ✅ Collection filtering (including 'sale' special handling)
 * 4. ✅ Sub-category filtering (Tops, Dresses, Bottoms, Jewelry)
 * 5. ✅ Price range slider filtering
 * 6. ✅ Size filtering with size groups
 * 7. ✅ Color filtering
 * 8. ✅ Out-of-stock toggle
 * 9. ✅ Multiple sort options
 * 10. ✅ Pagination with page size control
 * 11. ✅ Grid column control (2/3/4)
 * 12. ✅ Scroll position restoration
 * 13. ✅ Mobile filter sidebar
 * 14. ✅ Loading states with skeleton cards
 * 15. ✅ Empty state messaging
 * 16. ✅ Quick view modal support
 * 17. ✅ Analytics tracking
 * 18. ✅ Focus management
 * 19. ✅ Collapsible filter sections
 * 20. ✅ Recently viewed integration
 * 21. ✅ Product recommendation engine integration
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { EnhancedShopifyProductCard } from "@/components/enhanced";
import { useShopifyQuery } from "@/hooks/useShopifyQuery";
import { ShopifyVariant, ShopifyProduct } from "@/hooks/useShopify";
import { TaxonomyFilter, TaxonomyFilterState } from "@/components/TaxonomyFilter";
import { CLOTHING_FILTER_NODES, ROOT_TAXONOMY, TaxonomyNode } from "@/config/taxonomy";

// Helper to find taxonomy node by handle (recursive search)
const findTaxonomyNodeByHandle = (nodes: TaxonomyNode[], handle: string): TaxonomyNode | undefined => {
  for (const node of nodes) {
    if (node.handle === handle) return node;
    if (node.children) {
      const found = findTaxonomyNodeByHandle(node.children, handle);
      if (found) return found;
    }
  }
  return undefined;
};

// Extend ShopifyProduct with matched variant tracking
interface EnhancedShopifyProduct extends ShopifyProduct {
  _matchedVariant?: ShopifyVariant | null;
}
import { useSelectiveShopify } from "@/hooks/useSelectiveShopify";
import { useProducts } from "@/hooks/useProducts";
import { useFuzzySearch } from '@/hooks/useFuzzySearch';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, ChevronDown, Loader2, Sparkles, Grid2x2, Grid3x3, LayoutGrid, ZoomOut, ZoomIn, AlignJustify } from "lucide-react";
import { productFocusManager } from "@/utils/productFocusManager";
import { searchAnalytics } from '@/utils/searchAnalytics';
import { productionLogger } from '@/utils/production-logger';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { getProductSizeGroups } from "@/utils/sizeGroups";
import { ProductCardSkeleton } from "@/components/LoadingSystem";
import { Slider } from "@/components/ui/slider";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useIsMobile } from "@/hooks/use-mobile";

export interface ProductGridManagerProps {
  // Data source configuration
  enableFullCatalog?: boolean;
  preFilteredProducts?: ShopifyProduct[];
  
  // Page layout configuration
  showHero?: boolean;
  heroContent?: React.ReactNode;
  showSearch?: boolean;
  showSidebar?: boolean;
  showSortControl?: boolean;
  showPagination?: boolean;
  
  // Filter configuration
  enablePriceFilter?: boolean;
  enableSizeFilter?: boolean;
  enableColorFilter?: boolean;
  enableStockFilter?: boolean;
  enableCategoryFilter?: boolean;
  enableSubCategoryFilters?: boolean;
  
  // Custom filtering
  preFilterFn?: (products: ShopifyProduct[]) => ShopifyProduct[];
  
  // URL sync configuration
  enableUrlSync?: boolean;
  urlPrefix?: string;
  
  // Scroll restoration
  enableScrollRestoration?: boolean;
  scrollTargetId?: string;
  
  // Display customization
  defaultViewSize?: 'small' | 'medium' | 'large';
  defaultColumnCount?: 2 | 3 | 4;
  defaultSortBy?: 'title' | 'price_asc' | 'price_desc' | 'newest' | 'oldest' | 'updated';
  productsPerPage?: number;
  hideCustomBadges?: boolean; // Hide "Featured", "Curated", etc. badges on product cards
  hideViewControls?: boolean; // Hide view size and column count controls
  
  // Custom rendering
  renderCustomFilters?: () => React.ReactNode;
  renderBeforeGrid?: () => React.ReactNode;
  renderAfterGrid?: () => React.ReactNode;
  
  // Callbacks
  onFilterChange?: (filters: any) => void;
  onProductsFiltered?: (products: ShopifyProduct[]) => void;
  
  // Collection context
  collectionHandle?: string;
  forceCategory?: string; // Force a specific category for filtering (overrides URL param)
  taxonomyRootCollection?: string; // Root collection for TaxonomyFilter (shows only children of this collection)
  taxonomyAllLabel?: string; // Custom label for "All Products" option (e.g., "All Apparel")
  
  // Class names for styling
  className?: string;
  gridClassName?: string;
  sidebarClassName?: string;
}

export const ProductGridManager = ({
  enableFullCatalog = true,
  preFilteredProducts,
  showHero = false,
  heroContent,
  showSearch = true,
  showSidebar = true,
  showSortControl = true,
  showPagination = true,
  enablePriceFilter = false, // Disabled until reliable price range data available
  enableSizeFilter = true,
  enableColorFilter = false, // Disabled until color metafields are implemented
  enableStockFilter = true,
  enableCategoryFilter = true,
  enableSubCategoryFilters = true,
  preFilterFn,
  enableUrlSync = true,
  urlPrefix = '',
  enableScrollRestoration = true,
  scrollTargetId = 'products',
  defaultViewSize = 'large',
  defaultColumnCount = 3,
  defaultSortBy = 'updated',
  productsPerPage = 24,
  hideCustomBadges = false,
  hideViewControls = false,
  renderCustomFilters,
  renderBeforeGrid,
  renderAfterGrid,
  onFilterChange,
  onProductsFiltered,
  collectionHandle,
  forceCategory,
  taxonomyRootCollection,
  taxonomyAllLabel,
  className = '',
  gridClassName = '',
  sidebarClassName = ''
}: ProductGridManagerProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  
  // Core filter states - initialize from URL if sync enabled
  const [searchQuery, setSearchQuery] = useState(enableUrlSync ? (searchParams.get('q') || '') : '');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(() => {
    if (forceCategory) return forceCategory;
    if (enableUrlSync) return searchParams.get('category') || undefined;
    return collectionHandle;
  });
  const [sortBy, setSortBy] = useState<'title' | 'price_asc' | 'price_desc' | 'newest' | 'oldest' | 'updated'>(() => {
    if (!enableUrlSync) return defaultSortBy;
    const sortParam = searchParams.get('sort');
    const validSorts = ['title', 'price_asc', 'price_desc', 'newest', 'oldest', 'updated'] as const;
    return validSorts.includes(sortParam as any) ? sortParam as typeof validSorts[number] : defaultSortBy;
  });
  const [currentPage, setCurrentPage] = useState(() => {
    if (!enableUrlSync) return 1;
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    // Always trust URL parameter for initial page state
    return Math.max(1, pageFromUrl);
  });
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Advanced filter states - using single max price slider
  // Read from URL if available, otherwise will be set to actual max when products load
  const [maxPrice, setMaxPrice] = useState<number>(() => {
    if (enableUrlSync) {
      const maxPriceParam = parseInt(searchParams.get('maxPrice') || '0');
      if (maxPriceParam > 0) return maxPriceParam;
    }
    return 2000; // Temporary default, will be updated to actual max
  });
  const [selectedSizes, setSelectedSizes] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const sizes = searchParams.get('sizes');
    return sizes ? sizes.split(',').filter(Boolean) : [];
  });
  const [selectedColors, setSelectedColors] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const colors = searchParams.get('colors');
    return colors ? colors.split(',').filter(Boolean) : [];
  });

  // Sub-category states
  const [selectedTopsSubCategories, setSelectedTopsSubCategories] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const subCats = searchParams.get('topsSubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showTopsSubCategories, setShowTopsSubCategories] = useState(() => selectedCategory === 'tops');
  
  const [selectedDressesSubCategories, setSelectedDressesSubCategories] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const subCats = searchParams.get('dressesSubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showDressesSubCategories, setShowDressesSubCategories] = useState(() => selectedCategory === 'dresses');
  
  const [selectedBottomsSubCategories, setSelectedBottomsSubCategories] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const subCats = searchParams.get('bottomsSubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showBottomsSubCategories, setShowBottomsSubCategories] = useState(() => selectedCategory === 'bottoms');
  
  const [selectedShoesSubCategories, setSelectedShoesSubCategories] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const subCats = searchParams.get('shoesSubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showShoesSubCategories, setShowShoesSubCategories] = useState(() => selectedCategory === 'shoes');
  
  const [selectedJewelrySubCategories, setSelectedJewelrySubCategories] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const subCats = searchParams.get('jewelrySubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showJewelrySubCategories, setShowJewelrySubCategories] = useState(() => selectedCategory === 'jewelry');
  
  // Taxonomy filter state (Phase 2 - replaces sub-category states when feature flag enabled)
  const [taxonomyFilter, setTaxonomyFilter] = useState<TaxonomyFilterState>(() => ({
    collection: enableUrlSync ? searchParams.get('taxCollection') || undefined : undefined,
    productType: enableUrlSync ? searchParams.get('taxProductType') || undefined : undefined,
    category: enableUrlSync ? searchParams.get('taxCategory') || undefined : undefined,
    tags: enableUrlSync ? (searchParams.get('taxTags')?.split(',').filter(Boolean) || []) : [],
  }));
  
  // Display preferences
  const { viewSize, columnCount, mobileColumns, setViewSize, setColumnCount, setMobileColumns } = useUserPreferences();
  
  // Loading states
  const [loadedImageCount, setLoadedImageCount] = useState(0);
  const [showCurating, setShowCurating] = useState(true);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  
  // Refs for scroll and URL sync
  const skipNextUrlSync = useRef(false);
  const skipTaxonomyUrlSync = useRef(false); // Prevent taxonomy URL sync loop
  const suppressScrollOnPageChange = useRef(false);
  const isPaginationClick = useRef(false); // Track if page change is from pagination interaction
  const location = useLocation();

  // Sync external prop changes to internal state (for controlled components)
  useEffect(() => {
    if (!enableUrlSync) {
      // When URL sync is disabled, allow external control
      setSortBy(defaultSortBy);
    }
  }, [defaultSortBy, enableUrlSync]);

  useEffect(() => {
    if (!enableUrlSync) {
      // When URL sync is disabled, allow external control of column count
      setColumnCount(defaultColumnCount);
    }
  }, [defaultColumnCount, enableUrlSync, setColumnCount]);

  // Sub-category configurations
  const topsSubCategories = ['Blouses', 'Camis', 'Cardigans', 'Hoodies', 'Jackets', 'Kimonos', 'Sweaters', 'T-Shirts', 'Tanks'];
  const dressesSubCategories = ['Mini Dresses'];

  // Data fetching hooks - get products AND loading state separately
  const shopifyQueryResult = useShopifyQuery({ enableFullCatalog });
  const { products: shopifyProducts, collections, searchProducts: searchShopify } = shopifyQueryResult;
  const { products: databaseProducts, loading: databaseLoading } = useProducts();
  
  // Sort collections in the specified order
  const sortedCollections = useMemo(() => {
    const order = [
      'tops',
      'bottoms',
      'dresses',
      'one-pieces',
      'accessories',
      'jewelry',
      'shoes',
      'bags',
      'bath-body',
      'home-decor',
      'office-stationery',
      'goddess',
      'chrome-hearts'
    ];
    
    // Filter out hot-chocolate-design as it's nested under shoes
    const filteredCollections = collections.filter(c => c.handle !== 'hot-chocolate-design');
    
    return [...filteredCollections].sort((a, b) => {
      const indexA = order.indexOf(a.handle);
      const indexB = order.indexOf(b.handle);
      
      // If both are in the order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // If only A is in the order array, it comes first
      if (indexA !== -1) return -1;
      
      // If only B is in the order array, it comes first
      if (indexB !== -1) return 1;
      
      // If neither are in the order array, maintain original order
      return 0;
    });
  }, [collections]);
  
  // Only wait for products, not collections (collections can load in background)
  const shopifyLoading = shopifyProducts.length === 0;
  
  // Combine data sources or use pre-filtered
  const products = useMemo(() => {
    if (preFilteredProducts) return preFilteredProducts;
    
    const combined = [...shopifyProducts];
    
    // Convert database products to Shopify format
    databaseProducts.forEach((dbProduct) => {
      const shopifyFormatProduct: ShopifyProduct = {
        id: `db_${dbProduct.id}`,
        title: dbProduct.name,
        handle: dbProduct.slug,
        description: dbProduct.description || '',
        productType: dbProduct.categories?.[0]?.name || 'General',
        tags: dbProduct.tags,
        vendor: 'Boho Style Co.',
        createdAt: dbProduct.created_at,
        updatedAt: dbProduct.updated_at,
        totalInventory: dbProduct.inventory_quantity,
        availableForSale: dbProduct.is_active && dbProduct.inventory_quantity > 0,
        images: dbProduct.images.map((url, index) => ({
          id: `${dbProduct.id}_img_${index}`,
          url: url,
          altText: dbProduct.name,
        })),
        variants: [{
          id: `${dbProduct.id}_variant`,
          title: 'Default',
          price: { amount: dbProduct.price.toString(), currencyCode: 'USD' },
          compareAtPrice: dbProduct.compare_price ? { amount: dbProduct.compare_price.toString(), currencyCode: 'USD' } : null,
          availableForSale: dbProduct.is_active && dbProduct.inventory_quantity > 0,
          quantityAvailable: dbProduct.inventory_quantity,
          selectedOptions: []
        }],
        options: [],
        priceRange: {
          minVariantPrice: { amount: dbProduct.price.toString(), currencyCode: 'USD' },
          maxVariantPrice: { amount: dbProduct.price.toString(), currencyCode: 'USD' }
        }
      };
      combined.push(shopifyFormatProduct);
    });
    
    return combined;
  }, [shopifyProducts, databaseProducts, preFilteredProducts]);

  // Calculate taxonomy filter counts from products
  const taxonomyCounts = useMemo(() => {
    const sourceProducts = preFilteredProducts || products;
    if (sourceProducts.length === 0) return {};
    
    const counts: Record<string, number> = {};
    
    // Helper to count products matching a tag label (case-insensitive)
    const countByTagLabel = (label: string) => {
      const labelLower = label.toLowerCase();
      return sourceProducts.filter(product => 
        product.tags?.some(tag => tag.toLowerCase() === labelLower) ||
        product.productType?.toLowerCase() === labelLower
      ).length;
    };
    
    // Recursively count for all taxonomy nodes
    // For parent nodes with children, count products with parent tag OR any child tag
    // For vendor nodes with tags array, count products matching any of those tags
    const countNode = (node: TaxonomyNode): number => {
      // Check if this is a vendor node with a tags array (e.g., footwear vendors)
      const nodeTags = (node as any).tags as string[] | undefined;
      if (nodeTags && nodeTags.length > 0) {
        // Vendor node: count products matching ANY of the vendor's tags
        const vendorTagsLower = nodeTags.map(t => t.toLowerCase());
        const count = sourceProducts.filter(product => 
          product.tags?.some(tag => vendorTagsLower.includes(tag.toLowerCase()))
        ).length;
        counts[node.handle] = count;
        return count;
      }
      
      if (node.children && node.children.length > 0) {
        // Parent node: collect ALL tag labels (parent + all descendants)
        const allLabels: string[] = [node.label.toLowerCase()];
        const collectLabels = (n: TaxonomyNode) => {
          allLabels.push(n.label.toLowerCase());
          // Also collect vendor tags if present
          const childTags = (n as any).tags as string[] | undefined;
          if (childTags) {
            childTags.forEach(t => allLabels.push(t.toLowerCase()));
          }
          n.children?.forEach(collectLabels);
        };
        node.children.forEach(collectLabels);
        
        // Count products matching ANY of these labels
        const total = sourceProducts.filter(product => 
          product.tags?.some(tag => allLabels.includes(tag.toLowerCase())) ||
          allLabels.includes(product.productType?.toLowerCase() || '')
        ).length;
        
        counts[node.handle] = total;
        // Still recurse to populate child counts
        node.children.forEach(countNode);
        return total;
      } else {
        // Leaf node: count by tag/productType label
        const count = countByTagLabel(node.label);
        counts[node.handle] = count;
        return count;
      }
    };
    
    // Count nodes based on which taxonomy is being displayed
    // For apparel collection or no root (full catalog), count CLOTHING_FILTER_NODES
    // For other collections, only count that collection's hierarchy
    if (taxonomyRootCollection === 'apparel') {
      CLOTHING_FILTER_NODES.forEach(countNode);
    } else if (!taxonomyRootCollection) {
      // Full catalog (/products page) - count ALL taxonomy nodes
      ROOT_TAXONOMY.forEach(countNode);
    } else {
      // Specific collection - just count CLOTHING_FILTER_NODES as fallback
      CLOTHING_FILTER_NODES.forEach(countNode);
    }
    
    return counts;
  }, [preFilteredProducts, products, taxonomyRootCollection]);

  // Calculate max price from all products
  const maxProductPrice = useMemo(() => {
    if (!products || products.length === 0) return 2000;
    const prices = products.map(p => parseFloat(p.priceRange?.maxVariantPrice?.amount || '0'));
    const calculatedMax = Math.max(...prices);
    return Math.ceil(calculatedMax / 100) * 100; // Round up to nearest 100
  }, [products]);

  // Track previous maxProductPrice to avoid overriding user changes
  const prevMaxProductPrice = useRef(2000);

  // Update max price when products load - only if not already set by URL or user
  useEffect(() => {
    // Only update if:
    // 1. Products just loaded (maxProductPrice changed from default)
    // 2. Current maxPrice is still at default OR equal to old maxProductPrice
    // This ensures we don't override user's slider position or URL params
    if (maxProductPrice !== 2000 && (maxPrice === 2000 || maxPrice === prevMaxProductPrice.current)) {
      setMaxPrice(maxProductPrice);
    }
    prevMaxProductPrice.current = maxProductPrice;
  }, [maxProductPrice, maxPrice]);

  // Fuzzy search integration
  const fuzzySearchOptions = useMemo(() => ({
    threshold: 0.2,
    keys: [
      { name: 'title', weight: 0.4 },
      { name: 'description', weight: 0.2 },
      { name: 'productType', weight: 0.3 },
      { name: 'tags', weight: 0.1 }
    ],
    exactWordMatch: ['midi', 'maxi', 'mini', 'crop', 'high-waisted', 'low-rise']
  }), []);

  const {
    search: fuzzySearch,
    searchResults,
    clearSearch: clearFuzzySearch
  } = useFuzzySearch(products, fuzzySearchOptions);

  // Loading state management
  const productsLoading = shopifyLoading || databaseLoading;

  useEffect(() => {
    if (products.length > 0) {
      setHasInitialLoad(true);
      const timer = setTimeout(() => setShowCurating(false), 100);
      return () => clearTimeout(timer);
    }
  }, [products.length]);

  // Trigger fuzzy search when query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      fuzzySearch(searchQuery);
      searchAnalytics.trackSearch(searchQuery, 0); // Track with 0 results initially, update after filtering
    } else {
      clearFuzzySearch();
    }
  }, [searchQuery, fuzzySearch, clearFuzzySearch]);

  // Filter and sort products
  const allFilteredProducts = useMemo(() => {
    // Start with fuzzy search results or all products
    let baseFilteredProducts = searchQuery.trim() 
      ? searchResults.map(result => result.item)
      : [...products];

    // Apply pre-filter function if provided
    if (preFilterFn) {
      baseFilteredProducts = preFilterFn(baseFilteredProducts);
    }

    // Taxonomy-based filtering
    // Filter by collection - SKIP if preFilteredProducts provided (products already scoped by parent)
    if (taxonomyFilter.collection && !preFilteredProducts) {
      const collection = sortedCollections.find(c => c.handle === taxonomyFilter.collection);
      if (collection?.productHandles?.length) {
        baseFilteredProducts = baseFilteredProducts.filter(product =>
          collection.productHandles!.includes(product.handle)
        );
      } else {
        // Fallback to tag matching if collection data not loaded
        const collectionLabel = taxonomyFilter.collection
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        baseFilteredProducts = baseFilteredProducts.filter(product =>
          product.tags?.some(tag => tag.toLowerCase() === collectionLabel.toLowerCase())
        );
      }
    }
    
    // Filter by productType - match products with tags from child categories
    if (taxonomyFilter.productType) {
      const productTypeNode = CLOTHING_FILTER_NODES?.find(
        (n: any) => n.handle === taxonomyFilter.productType
      );
      
      // Collect all valid tag labels from child categories (using the label field directly)
      const validTagLabels: Set<string> = new Set();
      if (productTypeNode?.children) {
        const collectLabels = (nodes: any[]) => {
          nodes.forEach((node: any) => {
            // Use the label directly from taxonomy config (EXACT match only)
            validTagLabels.add(node.label.toLowerCase());
            // Recurse into children (for nested tags)
            if (node.children) {
              collectLabels(node.children);
            }
          });
        };
        collectLabels(productTypeNode.children);
      }
      
      // Also add the productType label itself
      const productTypeLabel = productTypeNode?.label?.toLowerCase() || taxonomyFilter.productType.toLowerCase();
      validTagLabels.add(productTypeLabel);
      
      baseFilteredProducts = baseFilteredProducts.filter(product => {
        // Check productType field first (exact match)
        if (product.productType?.toLowerCase() === productTypeLabel) {
          return true;
        }
        // Check if any product tag EXACTLY matches our valid labels (no substring matching!)
        const hasMatchingTag = product.tags?.some(tag => validTagLabels.has(tag.toLowerCase()));
        return hasMatchingTag;
      });
    }
    
    // Filter by category (maps to Shopify tag)
    // BUT skip category filter if we have tags that are children of that category
    // (products with "Maxi Skirts" tag shouldn't also need "Skirts" tag)
    if (taxonomyFilter.category && taxonomyFilter.tags.length === 0) {
      // Look up node from taxonomy - check both CLOTHING_FILTER_NODES and ROOT_TAXONOMY
      let categoryNode = findTaxonomyNodeByHandle(CLOTHING_FILTER_NODES, taxonomyFilter.category);
      if (!categoryNode) {
        categoryNode = findTaxonomyNodeByHandle(ROOT_TAXONOMY, taxonomyFilter.category);
      }
      
      // Handle vendor nodes - match against vendor's tags array
      if (categoryNode?.type === 'vendor' && categoryNode.tags && Array.isArray(categoryNode.tags)) {
        const vendorTags = categoryNode.tags.map((t: string) => t.toLowerCase());
        baseFilteredProducts = baseFilteredProducts.filter(product =>
          product.tags?.some(tag => vendorTags.includes(tag.toLowerCase()))
        );
      } else if (categoryNode) {
        // Standard category filtering with found node - match label against product tags
        const categoryLabel = categoryNode.label;
        baseFilteredProducts = baseFilteredProducts.filter(product =>
          product.tags?.some(tag => tag.toLowerCase() === categoryLabel.toLowerCase())
        );
      } else {
        // No node found by handle - taxCategory might be a direct tag label (e.g., "Leather Loafers")
        // This handles Footwear style filters which pass tag names directly
        const categoryLabel = taxonomyFilter.category;
        baseFilteredProducts = baseFilteredProducts.filter(product =>
          product.tags?.some(tag => tag.toLowerCase() === categoryLabel.toLowerCase())
        );
      }
    }
    
    // Filter by selected tags (multi-select, OR logic)
    if (taxonomyFilter.tags.length > 0) {
      // Look up actual labels from taxonomy (preserves hyphens like "Button-Up Blouses")
      // If tag contains spaces, it's already a full label (e.g., "Mary Jane Flats" from vendor groupings)
      const tagLabels = taxonomyFilter.tags.map(tagOrHandle => {
        // If it contains spaces, it's already a full label - use as-is
        if (tagOrHandle.includes(' ')) {
          return tagOrHandle;
        }
        // Otherwise, look up in taxonomy or convert from kebab-case
        const node = findTaxonomyNodeByHandle(CLOTHING_FILTER_NODES, tagOrHandle);
        return node?.label || tagOrHandle.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      });
      
      baseFilteredProducts = baseFilteredProducts.filter(product =>
        product.tags?.some(tag => {
          const tagLower = tag.toLowerCase();
          return tagLabels.some(label => tagLower === label.toLowerCase());
        })
      );
    }

    // Sale filtering
    if (selectedCategory === 'sale') {
      baseFilteredProducts = baseFilteredProducts.filter(product => {
        const hasVariantsOnSale = product.variants?.some(variant => {
          const comparePrice = variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : 0;
          const regularPrice = parseFloat(variant.price.amount);
          return comparePrice > regularPrice;
        });
        const mainComparePrice = product.compareAtPriceRange?.minVariantPrice ? parseFloat(product.compareAtPriceRange.minVariantPrice.amount) : 0;
        const mainPrice = parseFloat(product.priceRange?.minVariantPrice?.amount || '0');
        const hasMainSale = mainComparePrice > mainPrice;
        return hasVariantsOnSale || hasMainSale;
      });
    }

    // Sort products
    switch (sortBy) {
      case 'title':
        baseFilteredProducts.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'price_asc':
        baseFilteredProducts.sort((a, b) => parseFloat(a.variants[0]?.price?.amount || '0') - parseFloat(b.variants[0]?.price?.amount || '0'));
        break;
      case 'price_desc':
        baseFilteredProducts.sort((a, b) => parseFloat(b.variants[0]?.price?.amount || '0') - parseFloat(a.variants[0]?.price?.amount || '0'));
        break;
      case 'newest':
        baseFilteredProducts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        baseFilteredProducts.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case 'updated':
      default:
        baseFilteredProducts.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }

    // Apply additional filters with variant tracking
    const filtered = baseFilteredProducts.map((product) => {
      let matchedVariant: ShopifyVariant | null = null;

      // Price filtering - show products whose price range overlaps with selected range
      if (enablePriceFilter) {
        const productMinPrice = parseFloat(product.priceRange?.minVariantPrice?.amount || '0');
        // Filter out products above the max price threshold
        if (productMinPrice > maxPrice) {
          return null;
        }
      }

      // Size filtering
      if (enableSizeFilter && selectedSizes.length > 0) {
        let hasMatchingSize = false;
        selectedSizes.forEach(selectedSize => {
          if (hasMatchingSize || matchedVariant) return;
          product.variants?.forEach(variant => {
            if (hasMatchingSize || matchedVariant) return;
            const variantMatches = variant.selectedOptions?.some(option =>
              (option.name?.toLowerCase().includes('size') || option.name?.toLowerCase().includes('length')) &&
              option.value?.toLowerCase().includes(selectedSize.toLowerCase())
            );
            if (variantMatches) {
              matchedVariant = variant;
              hasMatchingSize = true;
            }
          });
        });
        if (!hasMatchingSize && !selectedSizes.some(selectedSizeGroup => getProductSizeGroups(product).includes(selectedSizeGroup))) {
          return null;
        }
      }

      // Color filtering - check both variant options AND product tags
      if (enableColorFilter && selectedColors.length > 0) {
        let hasMatchedColor = false;
        selectedColors.forEach(selectedColor => {
          if (hasMatchedColor) return;
          // Check variant color options
          product.variants?.forEach(variant => {
            if (hasMatchedColor) return;
            const variantMatches = variant.selectedOptions?.some(option =>
              option.name?.toLowerCase().includes('color') &&
              option.value?.toLowerCase().includes(selectedColor.toLowerCase())
            );
            if (variantMatches) {
              matchedVariant = variant;
              hasMatchedColor = true;
            }
          });
          // Also check product tags for color matches
          if (!hasMatchedColor && product.tags?.some(tag => 
            tag.toLowerCase().includes(selectedColor.toLowerCase())
          )) {
            hasMatchedColor = true;
          }
        });
        if (!hasMatchedColor) return null;
      }

      return { ...product, _matchedVariant: matchedVariant };
    }).filter(Boolean) as EnhancedShopifyProduct[];

    // Notify callback
    if (onProductsFiltered) {
      onProductsFiltered(filtered);
    }

    // Update search analytics
    if (searchQuery.trim()) {
      searchAnalytics.trackSearch(searchQuery, filtered.length);
    }

    return filtered;
  }, [
    products, searchQuery, searchResults, selectedCategory, sortBy, maxPrice, selectedSizes, selectedColors,
    selectedTopsSubCategories, selectedDressesSubCategories, selectedBottomsSubCategories, selectedShoesSubCategories,
    sortedCollections, preFilterFn, enablePriceFilter, enableSizeFilter, enableColorFilter, enableStockFilter,
    onProductsFiltered, taxonomyFilter
  ]);

  // Calculate product count per collection for disabling empty collections
  const collectionProductCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    
    sortedCollections.forEach(collection => {
      const categoryLower = collection.handle.toLowerCase();
      const count = products.filter(product => {
        const inCollection = collection.productHandles?.includes(product.handle) || false;
        const hasMatchingTag = product.tags.some(tag => {
          const tagLower = tag.toLowerCase();
          return tagLower === categoryLower ||
            (categoryLower === 'tops' && ['blouses', 'shirts', 'sweaters', 'cardigans', 'tunics'].includes(tagLower)) ||
            (categoryLower === 'bottoms' && ['pants', 'jeans', 'shorts', 'skirts'].includes(tagLower)) ||
            (categoryLower === 'dresses' && tagLower === 'dress') ||
            (categoryLower === 'jewelry' && ['necklaces', 'earrings', 'bracelets', 'rings'].includes(tagLower)) ||
            (categoryLower === 'accessories' && ['scarves', 'hats', 'belts'].includes(tagLower));
        });
        return inCollection || hasMatchingTag;
      }).length;
      
      counts[collection.handle] = count;
    });
    
    return counts;
  }, [products, sortedCollections]);

  // Pagination
  const totalPages = Math.ceil(allFilteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const filteredProducts = allFilteredProducts.slice(startIndex, endIndex);
  
  // Reset to page 1 if current page exceeds total pages
  // BUT: Don't reset if we're still loading or if this is back navigation
  useEffect(() => {
    const isBackNav = sessionStorage.getItem('isBackNavigation') === 'true';
    if (!isBackNav && !productsLoading && currentPage > 1 && currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages, productsLoading]);

  // Reset loaded image count when products change
  useEffect(() => {
    setLoadedImageCount(0);
  }, [filteredProducts.length]);

  // URL sync
  useEffect(() => {
    if (!enableUrlSync) return;
    
    const params = new URLSearchParams(location.search);
    const nextQ = params.get('q') || '';
    const nextCategory = forceCategory || params.get('category') || undefined;
    const sortParam = params.get('sort');
    const validSorts = ['title', 'price_asc', 'price_desc', 'newest', 'oldest', 'updated'] as const;
    const nextSort = validSorts.includes(sortParam as any) ? sortParam as typeof validSorts[number] : defaultSortBy;
    const nextPage = parseInt(params.get('page') || '1');
    const nextMaxPrice = parseInt(params.get('maxPrice') || maxProductPrice.toString());
    const nextSizes = params.get('sizes') ? params.get('sizes')!.split(',').filter(Boolean) : [];
    const nextColors = params.get('colors') ? params.get('colors')!.split(',').filter(Boolean) : [];
    const nextTopsSubCategories = params.get('topsSubCategories') ? params.get('topsSubCategories')!.split(',').filter(Boolean) : [];
    const nextDressesSubCategories = params.get('dressesSubCategories') ? params.get('dressesSubCategories')!.split(',').filter(Boolean) : [];
    const nextBottomsSubCategories = params.get('bottomsSubCategories') ? params.get('bottomsSubCategories')!.split(',').filter(Boolean) : [];
    const nextShoesSubCategories = params.get('shoesSubCategories') ? params.get('shoesSubCategories')!.split(',').filter(Boolean) : [];
    const nextJewelrySubCategories = params.get('jewelrySubCategories') ? params.get('jewelrySubCategories')!.split(',').filter(Boolean) : [];
    
    const basicChanged = nextQ !== searchQuery || nextCategory !== selectedCategory || nextSort !== sortBy || nextPage !== currentPage;
    const advancedChanged = nextMaxPrice !== maxPrice ||
      JSON.stringify(nextSizes) !== JSON.stringify(selectedSizes) || 
      JSON.stringify(nextColors) !== JSON.stringify(selectedColors) ||
      JSON.stringify(nextTopsSubCategories) !== JSON.stringify(selectedTopsSubCategories) ||
      JSON.stringify(nextDressesSubCategories) !== JSON.stringify(selectedDressesSubCategories) ||
      JSON.stringify(nextBottomsSubCategories) !== JSON.stringify(selectedBottomsSubCategories) ||
      JSON.stringify(nextShoesSubCategories) !== JSON.stringify(selectedShoesSubCategories) ||
      JSON.stringify(nextJewelrySubCategories) !== JSON.stringify(selectedJewelrySubCategories);

    if (basicChanged || advancedChanged) {
      skipNextUrlSync.current = true;
      setSearchQuery(nextQ);
      if (!forceCategory) setSelectedCategory(nextCategory);
      setSortBy(nextSort);
      setCurrentPage(nextPage);
      setMaxPrice(nextMaxPrice);
      setSelectedSizes(nextSizes);
      setSelectedColors(nextColors);
      setSelectedTopsSubCategories(nextTopsSubCategories);
      setSelectedDressesSubCategories(nextDressesSubCategories);
      setSelectedBottomsSubCategories(nextBottomsSubCategories);
      setSelectedShoesSubCategories(nextShoesSubCategories);
      setSelectedJewelrySubCategories(nextJewelrySubCategories);
    }
    
    // Sync taxonomy filter from URL params
    const nextTaxProductType = params.get('taxProductType') || undefined;
    const nextTaxCategory = params.get('taxCategory') || undefined;
    const nextTaxCollection = params.get('taxCollection') || undefined;
    const nextTaxTags = params.get('taxTags')?.split(',').filter(Boolean) || [];
    
    // Check if current taxonomy state differs from URL
    const taxonomyChanged = 
      taxonomyFilter.productType !== nextTaxProductType ||
      taxonomyFilter.category !== nextTaxCategory ||
      taxonomyFilter.collection !== nextTaxCollection ||
      JSON.stringify(taxonomyFilter.tags) !== JSON.stringify(nextTaxTags);
    
    if (taxonomyChanged) {
      // Mark that we're syncing from URL to prevent the write effect from running
      skipTaxonomyUrlSync.current = true;
      setTaxonomyFilter({
        collection: nextTaxCollection,
        productType: nextTaxProductType,
        category: nextTaxCategory,
        tags: nextTaxTags,
      });
    }
  }, [location.search, enableUrlSync]);

  useEffect(() => {
    if (!enableUrlSync || skipNextUrlSync.current) {
      skipNextUrlSync.current = false;
      return;
    }
    
    // Skip if we just synced taxonomy from URL (prevents loop)
    if (skipTaxonomyUrlSync.current) {
      skipTaxonomyUrlSync.current = false;
      return;
    }
    
    // Don't update URL if this is back navigation - preserve browser history
    const isBackNav = sessionStorage.getItem('isBackNavigation') === 'true';
    if (isBackNav) {
      return;
    }
    
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    // Only write category to URL if it differs from forceCategory (avoids redundant params)
    if (selectedCategory && selectedCategory !== forceCategory) params.set('category', selectedCategory);
    if (sortBy !== defaultSortBy) params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (maxPrice < maxProductPrice) params.set('maxPrice', maxPrice.toString());
    if (selectedSizes.length > 0) params.set('sizes', selectedSizes.join(','));
    if (selectedColors.length > 0) params.set('colors', selectedColors.join(','));
    if (selectedTopsSubCategories.length > 0) params.set('topsSubCategories', selectedTopsSubCategories.join(','));
    if (selectedDressesSubCategories.length > 0) params.set('dressesSubCategories', selectedDressesSubCategories.join(','));
    if (selectedBottomsSubCategories.length > 0) params.set('bottomsSubCategories', selectedBottomsSubCategories.join(','));
    if (selectedShoesSubCategories.length > 0) params.set('shoesSubCategories', selectedShoesSubCategories.join(','));
    if (selectedJewelrySubCategories.length > 0) params.set('jewelrySubCategories', selectedJewelrySubCategories.join(','));
    
    // Preserve taxonomy params
    if (taxonomyFilter.productType) params.set('taxProductType', taxonomyFilter.productType);
    if (taxonomyFilter.category) params.set('taxCategory', taxonomyFilter.category);
    if (taxonomyFilter.collection) params.set('taxCollection', taxonomyFilter.collection);
    if (taxonomyFilter.tags.length > 0) params.set('taxTags', taxonomyFilter.tags.join(','));

    const newString = params.toString();
    const currentString = window.location.search.replace(/^\?/, '');

    if (newString !== currentString) {
      setSearchParams(params, { replace: true });
    }
  }, [
    searchQuery, selectedCategory, sortBy, currentPage, maxPrice, selectedSizes, selectedColors,
    selectedTopsSubCategories, selectedDressesSubCategories, selectedBottomsSubCategories, selectedShoesSubCategories, selectedJewelrySubCategories,
    enableUrlSync, setSearchParams, defaultSortBy, taxonomyFilter, forceCategory
  ]);

  // Sub-category expansion handlers
  useEffect(() => {
    setShowTopsSubCategories(selectedCategory === 'tops');
    if (selectedCategory !== 'tops' && selectedTopsSubCategories.length > 0) setSelectedTopsSubCategories([]);
  }, [selectedCategory]);

  useEffect(() => {
    setShowDressesSubCategories(selectedCategory === 'dresses');
    if (selectedCategory !== 'dresses' && selectedDressesSubCategories.length > 0) setSelectedDressesSubCategories([]);
  }, [selectedCategory]);

  useEffect(() => {
    setShowBottomsSubCategories(selectedCategory === 'bottoms');
    if (selectedCategory !== 'bottoms' && selectedBottomsSubCategories.length > 0) setSelectedBottomsSubCategories([]);
  }, [selectedCategory]);

  useEffect(() => {
    setShowShoesSubCategories(selectedCategory === 'shoes');
    if (selectedCategory !== 'shoes' && selectedShoesSubCategories.length > 0) setSelectedShoesSubCategories([]);
  }, [selectedCategory]);

  useEffect(() => {
    setShowJewelrySubCategories(selectedCategory === 'jewelry');
    if (selectedCategory !== 'jewelry' && selectedJewelrySubCategories.length > 0) setSelectedJewelrySubCategories([]);
  }, [selectedCategory]);

  // Scroll restoration - improved back navigation handling
  useEffect(() => {
    if (!enableScrollRestoration) return;

    const saveScrollPosition = () => {
      const scrollKey = `scrollPos_${location.pathname}`;
      sessionStorage.setItem(scrollKey, window.scrollY.toString());
      sessionStorage.setItem(`${scrollKey}_timestamp`, Date.now().toString());
    };

    const restoreScrollPosition = () => {
      const isBackNav = sessionStorage.getItem('isBackNavigation') === 'true';
      const scrollKey = `scrollPos_${location.pathname}`;
      const savedPosition = sessionStorage.getItem(scrollKey);
      const timestamp = sessionStorage.getItem(`${scrollKey}_timestamp`);
      
      // Restore if: (1) back navigation flag is set, (2) position was saved, (3) saved within last 5 minutes
      const isFreshSave = timestamp && (Date.now() - parseInt(timestamp)) < 300000; // 5 minutes
      
      // Restore scroll if:
      // 1. Back navigation flag is set, OR
      // 2. Page reload on page 2+ (currentPage > 1 from URL)
      const isPageReload = currentPage > 1 && !isPaginationClick.current;
      
      if ((isBackNav || isPageReload) && savedPosition && isFreshSave && !suppressScrollOnPageChange.current) {
        // Wait for products to finish loading AND for DOM to be ready
        const attemptRestore = () => {
          if (!productsLoading && filteredProducts.length > 0) {
            setTimeout(() => {
              window.scrollTo(0, parseInt(savedPosition, 10));
              // Clean up flags AFTER successful restoration
              sessionStorage.removeItem('isBackNavigation');
              sessionStorage.removeItem(scrollKey);
              sessionStorage.removeItem(`${scrollKey}_timestamp`);
            }, 100); // Small delay to ensure grid is rendered
          }
        };

        // If products are still loading, wait for them
        if (productsLoading || filteredProducts.length === 0) {
          const checkInterval = setInterval(() => {
            if (!productsLoading && filteredProducts.length > 0) {
              clearInterval(checkInterval);
              attemptRestore();
            }
          }, 50);
          
          // Timeout after 3 seconds
          setTimeout(() => clearInterval(checkInterval), 3000);
        } else {
          attemptRestore();
        }
      }
    };

    restoreScrollPosition();
    window.addEventListener('beforeunload', saveScrollPosition);

    // ALSO save periodically when on page 2+ (ensures position is always available on reload)
    let saveInterval: number | undefined;
    if (currentPage > 1) {
      saveInterval = window.setInterval(saveScrollPosition, 2000); // Save every 2 seconds
    }

    return () => {
      window.removeEventListener('beforeunload', saveScrollPosition);
      if (saveInterval) clearInterval(saveInterval);
    };
  }, [location.pathname, enableScrollRestoration, productsLoading, filteredProducts.length, currentPage]);

  useEffect(() => {
    if (!enableScrollRestoration || suppressScrollOnPageChange.current) {
      suppressScrollOnPageChange.current = false;
      return;
    }

    // On mobile: ALWAYS scroll to top on pagination
    // On desktop: Only scroll if page change came from pagination interaction
    const isMobileDevice = window.innerWidth < 768;
    if (!isMobileDevice && !isPaginationClick.current) {
      return;
    }

    // Reset the flag after handling
    isPaginationClick.current = false;

    const targetElement = document.getElementById(scrollTargetId);
    
    if (targetElement) {
      const elementTop = targetElement.getBoundingClientRect().top + window.scrollY;
      const offset = 30;
      
      window.scrollTo({
        top: Math.max(0, elementTop - offset),
        behavior: 'smooth'
      });
    }
  }, [currentPage, enableScrollRestoration, scrollTargetId]);

  // Grid layout utilities
  const getGridClasses = () => {
    const mobileClass = mobileColumns === 2 ? 'grid-cols-2' : 'grid-cols-1';
    
    if (viewSize === 'large') {
      switch (columnCount) {
        case 2: return `${mobileClass} lg:grid-cols-2`;
        case 3: return `${mobileClass} sm:grid-cols-2 lg:grid-cols-3`;
        case 4: return `${mobileClass} sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4`;
      }
    } else if (viewSize === 'gallery') {
      switch (columnCount) {
        case 2: return `${mobileClass} sm:grid-cols-2`;
        case 3: return `${mobileClass} sm:grid-cols-2 lg:grid-cols-3`;
        case 4: return `${mobileClass} sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4`;
      }
    } else {
      switch (columnCount) {
        case 2: return `${mobileClass} sm:grid-cols-2`;
        case 3: return `${mobileClass} sm:grid-cols-2 md:grid-cols-3`;
        case 4: return `${mobileClass} sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4`;
      }
    }
  };

  const getGapClass = () => {
    if (columnCount === 2) {
      if (viewSize === 'small') return 'gap-x-2 gap-y-3';
      if (viewSize === 'gallery') return 'gap-x-4 gap-y-5';
      if (viewSize === 'large') return 'gap-x-5 gap-y-6';
    }
    return 'gap-x-2 gap-y-3';
  };

  // Clear all filters
  const clearAllFilters = () => {
    suppressScrollOnPageChange.current = true;
    setSearchQuery('');
    clearFuzzySearch();
    setSelectedCategory(collectionHandle);
    setSortBy(defaultSortBy);
    setCurrentPage(1);
    setMaxPrice(maxProductPrice);
    setSelectedSizes([]);
    setSelectedColors([]);
    setSelectedTopsSubCategories([]);
    setSelectedDressesSubCategories([]);
    setSelectedBottomsSubCategories([]);
    setSelectedShoesSubCategories([]);
    setSelectedJewelrySubCategories([]);

    if (onFilterChange) {
      onFilterChange({
        searchQuery: '',
        selectedCategory: collectionHandle,
        sortBy: defaultSortBy,
        maxPrice: maxProductPrice,
        selectedSizes: [],
        selectedColors: []
      });
    }
  };

  // Color filter data - using muted palette from design system
  const colorOptions = [
    { name: 'White', color: 'hsl(0, 0%, 95%)' },
    { name: 'Black', color: 'hsl(0, 0%, 10%)' },
    { name: 'Red', color: 'hsl(0, 45%, 55%)' },
    { name: 'Blue', color: 'hsl(210, 40%, 55%)' },
    { name: 'Green', color: 'hsl(140, 35%, 50%)' },
    { name: 'Yellow', color: 'hsl(45, 60%, 65%)' },
    { name: 'Pink', color: 'hsl(330, 45%, 70%)' },
    { name: 'Purple', color: 'hsl(270, 35%, 55%)' },
    { name: 'Orange', color: 'hsl(25, 55%, 60%)' },
    { name: 'Brown', color: 'hsl(25, 40%, 45%)' },
    { name: 'Gray', color: 'hsl(0, 0%, 50%)' },
    { name: 'Gold', color: 'hsl(45, 60%, 55%)' },
    { name: 'Silver', color: 'hsl(0, 0%, 65%)' }
  ];

  return (
    <div className={className}>
      {/* Hero Section */}
      {showHero && heroContent && <div className="mb-6">{heroContent}</div>}

      <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
        {/* Sidebar Filters */}
        {showSidebar && (
          <div className={`w-full lg:w-[200px] flex-shrink-0 space-y-2 md:space-y-6 lg:sticky lg:top-4 lg:self-start lg:z-20 ${sidebarClassName}`}>
            <div className="bg-card rounded-lg border border-border/50">
              {/* Mobile Toggle */}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="lg:hidden w-full p-3 flex items-center justify-between text-left hover:bg-muted/50 rounded-t transition-colors"
              >
                <h3 className="font-medium text-foreground text-sm">Filter Products</h3>
                <ChevronDown className={`h-4 w-4 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} />
              </button>

              {/* Desktop Title */}
              <div className="hidden lg:block p-4 border-b border-border/30">
                <h3 className="font-medium text-foreground text-base">Filter Products</h3>
              </div>


              {/* Filter Content */}
              <div className={`${showMobileFilters ? 'block' : 'hidden'} lg:block p-3 lg:p-4 space-y-4`}>
                {/* Taxonomy Category Filter */}
                {enableCategoryFilter && (
                  <TaxonomyFilter
                    value={taxonomyFilter}
                    onChange={(newState) => {
                      setTaxonomyFilter(newState);
                      // Sync taxonomy collection to legacy selectedCategory for compatibility
                      if (newState.collection) {
                        setSelectedCategory(newState.collection);
                      } else if (newState.productType) {
                        // Map productType to collection if possible
                        const productTypeToCollection: Record<string, string> = {
                          'tops': 'tops',
                          'bottoms': 'bottoms',
                          'outfit-sets': 'apparel',
                          'loungewear-type': 'loungewear',
                          'dresses-type': 'dresses',
                          'one-pieces-type': 'one-pieces',
                        };
                        setSelectedCategory(productTypeToCollection[newState.productType] || undefined);
                      } else {
                        setSelectedCategory(undefined);
                      }
                    }}
                    onPageReset={() => setCurrentPage(1)}
                    rootCollection={taxonomyRootCollection}
                    counts={taxonomyCounts}
                    totalCount={preFilteredProducts?.length || products.length}
                    allLabel={taxonomyAllLabel}
                  />
                )}


                {/* Price Filter */}
                {enablePriceFilter && (
                  <div>
                    <Label className="text-sm font-medium mb-3 block">
                      Maximum Price: ${maxPrice}
                    </Label>
                    <Slider
                      value={[maxPrice]}
                      onValueChange={(value) => setMaxPrice(value[0])}
                      min={0}
                      max={maxProductPrice}
                      step={100}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>$0</span>
                      <span>${maxProductPrice}</span>
                    </div>
                  </div>
                )}

                {/* Color Filter */}
                {enableColorFilter && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Colors</label>
                    <div className="grid grid-cols-5 gap-2">
                      {colorOptions.map(color => {
                        const isSelected = selectedColors.includes(color.name.toLowerCase());
                        const needsLightOutline = ['White', 'Yellow'].includes(color.name);
                        return (
                          <button
                            key={color.name}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedColors(selectedColors.filter(c => c !== color.name.toLowerCase()));
                              } else {
                                setSelectedColors([...selectedColors, color.name.toLowerCase()]);
                              }
                            }}
                            className="p-0"
                            title={color.name}
                          >
                            <div
                              className={`w-8 h-8 rounded-full border-2 transition-all ${isSelected ? 'ring-1 ring-accent ring-offset-1 ring-offset-background' : 'hover:scale-105'} ${needsLightOutline ? 'border-border' : 'border-muted'}`}
                              style={{ backgroundColor: color.color }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Sort Filter */}
                {showSortControl && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Sort By</label>
                    <div className="space-y-2">
                      {[
                        { value: "updated", label: "Recently Updated" },
                        { value: "newest", label: "Season's Freshest" },
                        { value: "oldest", label: "Timeless Pieces" },
                        { value: "price_asc", label: "Price: Low to High" },
                        { value: "price_desc", label: "Price: High to Low" }
                      ].map(option => (
                        <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="sortBy"
                            checked={sortBy === option.value}
                            onChange={() => setSortBy(option.value as any)}
                            className="w-4 h-4 accent-primary"
                          />
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Filters */}
                {renderCustomFilters && renderCustomFilters()}

                {/* Clear Filters */}
                <Button onClick={clearAllFilters} variant="outline" className="w-full mt-4">
                  Clear All Filters
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Products Grid Column */}
        <div className="flex-1 min-w-0">
          {/* Before Grid Custom Content */}
          {renderBeforeGrid && renderBeforeGrid()}

          {/* Products Grid */}
          {(productsLoading || !hasInitialLoad) ? (
            <div className="space-y-4">
              {/* NO SCROLL ANCHOR HERE - only one anchor exists above search controls (line ~1230) */}
              <div className="text-center py-8">
                <div className="flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mr-3" />
                  <div>
                    <h3 className="text-xl font-semibold">Curating your selection...</h3>
                    <p className="text-muted-foreground mt-1">Finding the perfect pieces for you</p>
                  </div>
                </div>
              </div>
              <div className={`grid ${getGridClasses()} ${getGapClass()} ${gridClassName}`}>
                {Array.from({ length: productsPerPage }).map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold mb-2">No products found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? `No results for "${searchQuery}"` : 'Try adjusting your filters'}
              </p>
              <Button onClick={clearAllFilters} variant="outline">Clear All Filters</Button>
            </div>
          ) : (
            <>
              {/* View Controls & Results Count */}
              <div className="mb-4 pb-4 border-b border-border">
                {/* Scroll Anchor - positioned right before search controls */}
                <div id={scrollTargetId} className="-scroll-mt-20"></div>
                <div className="flex flex-wrap items-center justify-between gap-4 md:gap-3">
                  {/* Results Count - Left aligned */}
                  <div className="subsection-header flex-shrink-0">
                    {(() => {
                      // Only count as filtered if price is BELOW the calculated maximum
                      const isPriceFiltered = maxPrice < maxProductPrice;
                      
                      const hasAnyFilter = preFilteredProducts || 
                        collectionHandle || 
                        selectedCategory ||
                        searchQuery ||
                        selectedSizes.length > 0 ||
                        selectedColors.length > 0 ||
                        isPriceFiltered ||
                        selectedTopsSubCategories.length > 0 ||
                        selectedDressesSubCategories.length > 0 ||
                        selectedBottomsSubCategories.length > 0 ||
                        selectedShoesSubCategories.length > 0;
                      
                      return `${startIndex + 1}-${Math.min(endIndex, allFilteredProducts.length)} ${hasAnyFilter ? `of ${allFilteredProducts.length}` : 'of All Products'}${searchQuery ? ` for "${searchQuery}"` : ''}`;
                    })()}
                  </div>
                  
                  {/* Right side controls */}
                  <div className="flex flex-wrap items-center gap-3 md:gap-2">
                    {/* Search */}
                    {showSearch && (
                      <div className="min-w-[200px] max-w-xs">
                        <Input
                          type="search"
                          placeholder="Search this collection..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    )}
                    
                    {/* View Controls - Only show if not hidden */}
                    {!hideViewControls && (
                      <>
                        {/* View Size Controls */}
                        <div className="flex items-center gap-1 border border-border rounded-md p-1">
                          <Button
                            variant={viewSize === 'small' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setViewSize('small')}
                            className="h-8 px-2"
                            title="Small: Thumbnails"
                            aria-label="Small view"
                          >
                            <Grid2x2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant={viewSize === 'large' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setViewSize('large')}
                            className="h-8 px-2"
                            title="Large: Thumbnails"
                            aria-label="Large view"
                          >
                            <Grid2x2 className="h-4 w-4 scale-125" aria-hidden="true" />
                          </Button>
                          <Button
                            variant={viewSize === 'gallery' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setViewSize('gallery')}
                            className="h-8 px-2"
                            title="Gallery View"
                            aria-label="Gallery view"
                          >
                            <LayoutGrid className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                        {/* Column Count Controls */}
                        <div className="flex items-center gap-1 border border-border rounded-md p-1">
                        {isMobile ? (
                          <>
                            {/* Mobile: Show 1-column and 2-column buttons only */}
                            <Button
                              variant={mobileColumns === 1 ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => setMobileColumns(1)}
                              className="h-8 px-2"
                              title="1 column"
                              aria-label="1 column layout"
                            >
                              <AlignJustify className="h-4 w-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant={mobileColumns === 2 ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => setMobileColumns(2)}
                              className="h-8 px-2"
                              title="2 columns"
                              aria-label="2 column layout"
                            >
                              <Grid2x2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </>
                        ) : (
                          <>
                            {/* Desktop: Show 2, 3, 4 column buttons */}
                            <Button
                              variant={columnCount === 2 ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => setColumnCount(2)}
                              className="h-8 px-2"
                              title="2 columns"
                              aria-label="2 column layout"
                            >
                              <Grid2x2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant={columnCount === 3 ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => setColumnCount(3)}
                              className="h-8 px-2"
                              title="3 columns"
                              aria-label="3 column layout"
                            >
                              <Grid3x3 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant={columnCount === 4 ? 'default' : 'ghost'}
                              size="sm"
                              onClick={() => setColumnCount(4)}
                              className="h-8 px-2"
                              title="4 columns"
                              aria-label="4 column layout"
                            >
                              <LayoutGrid className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </>
                        )}
                        </div>
                    </>
                  )}
                </div>
              </div>
            </div>

              <ul className={`grid ${getGridClasses()} ${getGapClass()} ${gridClassName}`} role="list">
                {filteredProducts.map((product, index) => (
                  <li key={`${product.id}-${index}`}>
                    <EnhancedShopifyProductCard
                      product={product}
                      size={viewSize === 'large' ? 'large' : 'small'}
                      columnCount={columnCount}
                      viewSize={viewSize}
                      matchedVariant={product._matchedVariant}
                      showPurchaseControls={false}
                      hideCustomBadges={hideCustomBadges}
                      onImageLoad={index < 6 ? () => setLoadedImageCount(prev => prev + 1) : undefined}
                    />
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* After Grid Custom Content */}
          {renderAfterGrid && renderAfterGrid()}
        </div>
      </div>

      {/* Pagination - Outside flex container, spans full width */}
      {showPagination && totalPages > 1 && (() => {
        // MOBILE: Show max 5 pages, DESKTOP: Show max 10 pages
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
        const MAX_VISIBLE_PAGES = isMobile ? 5 : 10;
        
        // Calculate visible page range centered around current page
        let startPage = Math.max(1, currentPage - Math.floor(MAX_VISIBLE_PAGES / 2));
        let endPage = Math.min(totalPages, startPage + MAX_VISIBLE_PAGES - 1);
        
        // Adjust start if we're near the end
        if (endPage - startPage + 1 < MAX_VISIBLE_PAGES) {
          startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
        }
        
        const showFirstEllipsis = startPage > 2;
        const showLastEllipsis = endPage < totalPages - 1;
        const showFirst = startPage > 1;
        const showLast = endPage < totalPages;
        
        // Helper to generate URL with page number
        const getPageUrl = (page: number) => {
          const params = new URLSearchParams(searchParams);
          if (page === 1) {
            params.delete('page');
          } else {
            params.set('page', page.toString());
          }
          const queryString = params.toString();
          return queryString ? `?${queryString}` : location.pathname;
        };
        
        const handlePageClick = (page: number) => (e: React.MouseEvent) => {
          e.preventDefault();
          isPaginationClick.current = true;
          setCurrentPage(page);
        };
        
        return (
          <Pagination className="pt-8">
            <PaginationContent className="flex-wrap justify-center gap-1">
              {/* Previous Button */}
              <PaginationItem>
                <PaginationPrevious 
                  href={getPageUrl(Math.max(1, currentPage - 1))}
                  onClick={handlePageClick(Math.max(1, currentPage - 1))}
                  aria-disabled={currentPage === 1}
                  className={cn(
                    "px-2 sm:px-3",
                    currentPage === 1 ? 'pointer-events-none opacity-50' : ''
                  )}
                />
              </PaginationItem>
              
              {/* First Page */}
              {showFirst && (
                <PaginationItem className="hidden sm:block">
                  <PaginationLink
                    href={getPageUrl(1)}
                    onClick={handlePageClick(1)}
                    isActive={currentPage === 1}
                  >
                    1
                  </PaginationLink>
                </PaginationItem>
              )}
              
              {/* First Ellipsis */}
              {showFirstEllipsis && (
                <PaginationItem className="hidden sm:block">
                  <span className="px-2 text-muted-foreground">...</span>
                </PaginationItem>
              )}
              
              {/* Visible Page Numbers */}
              {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    href={getPageUrl(page)}
                    onClick={handlePageClick(page)}
                    isActive={currentPage === page}
                    className="min-w-[2.25rem] px-2"
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              
              {/* Last Ellipsis */}
              {showLastEllipsis && (
                <PaginationItem className="hidden sm:block">
                  <span className="px-2 text-muted-foreground">...</span>
                </PaginationItem>
              )}
              
              {/* Last Page */}
              {showLast && (
                <PaginationItem className="hidden sm:block">
                  <PaginationLink
                    href={getPageUrl(totalPages)}
                    onClick={handlePageClick(totalPages)}
                    isActive={currentPage === totalPages}
                  >
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              )}
              
              {/* Next Button */}
              <PaginationItem>
                <PaginationNext
                  href={getPageUrl(Math.min(totalPages, currentPage + 1))}
                  onClick={handlePageClick(Math.min(totalPages, currentPage + 1))}
                  aria-disabled={currentPage === totalPages}
                  className={cn(
                    "px-2 sm:px-3",
                    currentPage === totalPages ? 'pointer-events-none opacity-50' : ''
                  )}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        );
      })()}
    </div>
  );
};
